/**
 * Mixpanel analytics integration for the SaaS admin app.
 *
 * Env vars (build time via Vite):
 *   VITE_MIXPANEL_TOKEN  — required to enable (no-op if missing)
 */
import mixpanel, { type Dict } from 'mixpanel-browser'

let enabled = false

/** Initialise Mixpanel. Call once at app startup. */
export function initMixpanel(): void {
  const token = (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined) || '6cfa6134a5ee9c0817ae1820aa914027'
  if (!token) return

  const env =
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
    import.meta.env.MODE

  mixpanel.init(token, {
    debug: env !== 'production',
    track_pageview: true,
    persistence: 'localStorage',
    ip: true,
    api_host: 'https://api-eu.mixpanel.com',
    autocapture: true,
    record_sessions_percent: 100,
    ignore_dnt: true,
  } as any)

  enabled = true
}

interface AdminProfile {
  adminId: string
  email: string
  name: string
}

export const analytics = {
  /** Identify the logged-in platform admin. */
  identify(admin: AdminProfile): void {
    if (!enabled) return
    mixpanel.identify(admin.adminId)
    mixpanel.people.set({
      $name: admin.name,
      $email: admin.email,
      platform: 'web',
      app: 'admin',
    })
    mixpanel.register({ platform: 'web', app: 'admin' })
  },

  /** Reset identity on logout. */
  reset(): void {
    if (!enabled) return
    mixpanel.reset()
  },

  /** Track a named event with optional properties. */
  track(event: string, props?: Dict): void {
    if (!enabled) return
    mixpanel.track(event, props)
  },

  /** Track a page view. */
  pageView(pageName: string, path: string): void {
    if (!enabled) return
    mixpanel.track('Page Viewed', { page: pageName, path })
  },
}
